import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Joyride, STATUS, EVENTS, type ButtonType, type EventData, type Step } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface TourContextValue {
  startTour: (tourSteps: Step[], pageKey?: string | null) => void;
  startTourIfNew: (tourSteps: Step[], pageKey: string | undefined | null) => void;
  isTourCompleted: (pageKey: string) => boolean;
  markTourComplete: (pageKey: string) => void;
  resetAllTours: () => void;
  isTourActive: boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

interface StoredUser {
  id?: string;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [steps, setSteps] = useState<Step[]>([]);
  const [run, setRun] = useState(false);
  const currentPageKeyRef = useRef<string | null>(null);

  const getUserPrefix = useCallback((): string => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const user = JSON.parse(raw) as StoredUser;
        return user.id ? `user_${user.id}_` : '';
      }
    } catch {
      // Preserve the anonymous namespace for invalid stored user data.
    }
    return '';
  }, []);

  const isTourCompleted = useCallback(
    (pageKey: string): boolean => {
      const prefix = getUserPrefix();
      return localStorage.getItem(`${prefix}tour_completed_${pageKey}`) === 'true';
    },
    [getUserPrefix]
  );

  const markTourComplete = useCallback(
    (pageKey: string) => {
      const prefix = getUserPrefix();
      localStorage.setItem(`${prefix}tour_completed_${pageKey}`, 'true');
    },
    [getUserPrefix]
  );

  const startTour = useCallback((tourSteps: Step[], pageKey: string | null = null) => {
    if (!tourSteps || tourSteps.length === 0) return;
    setSteps(tourSteps);
    currentPageKeyRef.current = pageKey;
    setRun(true);
  }, []);

  const startTourIfNew = useCallback(
    (tourSteps: Step[], pageKey: string | undefined | null) => {
      if (!pageKey || isTourCompleted(pageKey)) return;
      startTour(tourSteps, pageKey);
    },
    [isTourCompleted, startTour]
  );

  const resetAllTours = useCallback(() => {
    const prefix = getUserPrefix();
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key && key.startsWith(`${prefix}tour_completed_`)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }, [getUserPrefix]);

  const handleEvent = useCallback(
    (data: EventData) => {
      const { status, type } = data;
      if (type === EVENTS.TOUR_END) {
        setRun(false);
        const pageKey = currentPageKeyRef.current;
        if (pageKey && (status === STATUS.FINISHED || status === STATUS.SKIPPED)) {
          markTourComplete(pageKey);
        }
      }
    },
    [markTourComplete]
  );

  const joyrideOptions = useMemo(
    () => ({
      primaryColor: theme.palette.primary.main,
      zIndex: 10000,
      arrowColor: theme.palette.background.paper,
      backgroundColor: theme.palette.background.paper,
      textColor: theme.palette.text.primary,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      showProgress: true,
      buttons: ['back', 'close', 'primary', 'skip'] as ButtonType[],
      overlayClickAction: 'close' as const,
    }),
    [theme]
  );

  const joyrideStyles = useMemo(
    () => ({
      tooltip: { borderRadius: theme.shape.borderRadius, padding: '16px 20px' },
      tooltipTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
      tooltipContent: { fontSize: 14, lineHeight: 1.5 },
      buttonPrimary: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius,
        fontSize: 13,
        fontWeight: 600,
        padding: '8px 16px',
      },
      buttonBack: { color: theme.palette.text.secondary, fontSize: 13, fontWeight: 600 },
      buttonSkip: { color: theme.palette.text.secondary, fontSize: 13 },
    }),
    [theme]
  );

  const value = useMemo<TourContextValue>(
    () => ({
      startTour,
      startTourIfNew,
      isTourCompleted,
      markTourComplete,
      resetAllTours,
      isTourActive: run,
    }),
    [startTour, startTourIfNew, isTourCompleted, markTourComplete, resetAllTours, run]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        steps={steps}
        run={run}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        options={joyrideOptions}
        styles={joyrideStyles}
        locale={{
          back: t('tour.locale.back', 'Anterior'),
          close: t('tour.locale.close', 'Cerrar'),
          last: t('tour.locale.last', 'Finalizar'),
          next: t('tour.locale.next', 'Siguiente'),
          skip: t('tour.locale.skip', 'Omitir'),
        }}
      />
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) throw new Error('useTour must be used within TourProvider');
  return context;
}

export default TourContext;